import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import PropTypes from 'prop-types';

import { AuthContext } from 'core/components/auth';
import { AssurityLifeModal } from 'common/components/assurity-life-modal/assurity-life-modal';
import useStyles from './additional-offers-section.styles';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { AssurityOffer } from 'common/components/assurity-offer/assurity-offer';
import { BranchAutoUpsell } from 'common/components/branch-auto-upsell';

export const AdditionalOffersSection = observer(function AdditionalOffersSection({
  assurityTermLife = false,
  assurityTermLifeData,
  bixAutoUpsellInput = null
}) {
  const {
    offer: { offer },
    additionalOffers
  } = useStore();
  const {
    user: { affinityCode }
  } = useSession();

  const location = window.location.href;
  const webUserId = offer?.webUserId;

  useEffect(() => {
    if (webUserId && webUserId !== additionalOffers?.webUserId) {
      additionalOffers.getAdditionalOfferData(webUserId);
    }
  }, [webUserId, additionalOffers]);

  const allPeople = [...(offer.quote.drivers || []), ...(offer.quote.nonDrivers || [])];

  const [showAssurityTermLifeModal, setShowAssurityTermLifeModal] = useState(false);

  const openAssurityTermLifeModal = async () => {
    setShowAssurityTermLifeModal(true);
    track('Agent Clicked View Life Offer', {
      URL: location,
      preBindId: webUserId,
      affinityCode
    });
  };

  const { viewOnly } = useContext(AuthContext);

  // The BIX upsell card can decide to hide itself after its on-mount mutation rejects
  // or errors — at which point we don't want a lonely "Additional Offers" header sitting
  // above nothing. Track that here so the header drops when the only thing left would
  // have been the upsell.
  //
  // `assurityWillRender` also pre-checks `eligibleLifeOffers.length > 0` because
  // `AssurityOffer` itself returns null when `personToDisplayLifeOfferFor` is unresolved
  // (e.g. no eligible offers, or the chosen person isn't in `allPeople`). Without this
  // guard, eligibility passes (`assurityTermLife === true`) but the component renders
  // null and we'd be left with an orphaned section header. Mirrors the customer-page
  // version.
  const [bixUpsellHidden, setBixUpsellHidden] = useState(false);
  const assurityWillRender = assurityTermLife && assurityTermLifeData?.eligibleLifeOffers?.length > 0;
  const bixUpsellWillRender = !!bixAutoUpsellInput && !bixUpsellHidden;
  const showSection = assurityWillRender || bixUpsellWillRender;

  const classes = useStyles();

  if (!showSection) {
    return null;
  }

  return (
    <>
      <Label type="darkGreenMedium" component="div" className={classes.sectionTitleLabel}>
        Additional Offers
      </Label>
      {assurityWillRender && (
        <AssurityOffer
          assurityTermLifeData={assurityTermLifeData}
          allPeople={allPeople}
          onOpenOffersClicked={openAssurityTermLifeModal}
          openOffersButtonDisabled={viewOnly}
        />
      )}
      {bixAutoUpsellInput && (
        <BranchAutoUpsell input={bixAutoUpsellInput} source="offer" onHide={() => setBixUpsellHidden(true)} />
      )}
      {showAssurityTermLifeModal && (
        <AssurityLifeModal
          open={showAssurityTermLifeModal}
          assurityData={assurityTermLifeData}
          onClose={() => setShowAssurityTermLifeModal(false)}
          people={allPeople}
        />
      )}
    </>
  );
});

AdditionalOffersSection.propTypes = {
  branchTermLife: PropTypes.bool,
  assurityTermLife: PropTypes.bool,
  assurityTermLifeData: PropTypes.object,
  bixAutoUpsellInput: PropTypes.object
};
